import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, FlaskConicalIcon } from 'lucide-react';
import { PortalHeader } from '../components/shell/PortalHeader';
import { JourneyStages } from '../components/shell/JourneyStages';
import { LoanOfficerChip } from '../components/shell/LoanOfficerChip';
import { Button } from '../components/ui/Button';
import { HelpPanel } from '../components/ui/HelpPanel';
import { usePortal } from '../contexts/PortalContext';
import { loanOptions } from '../data/loanOptions';
import { loanOfficer } from '../data/team';
import { formatCurrency } from '../utils/format';
import { getPropertyContext } from '../utils/propertyContext';
const ease = [0.23, 1, 0.32, 1] as const;
const compareRows: {
  label: string;
  get: (o: (typeof loanOptions)[number]) => string;
}[] = [{
  label: 'Monthly payment',
  get: o => `${formatCurrency(o.monthly)}`
}, {
  label: 'Interest rate',
  get: o => `${o.rate.toFixed(3)}%`
}, {
  label: 'APR',
  get: o => `${o.apr.toFixed(3)}%`
}, {
  label: 'Loan term',
  get: o => o.term
}, {
  label: 'Loan amount',
  get: o => formatCurrency(o.loanAmount)
}, {
  label: 'Points or credits',
  get: o => o.pointsLabel
}, {
  label: 'Est. cash to close',
  get: o => o.cashToClose != null ? formatCurrency(o.cashToClose) : 'Not available yet'
}];
export function LoanOptions() {
  const navigate = useNavigate();
  const {
    selectedOption: savedOption,
    selectOption,
    answers
  } = usePortal();
  const [compare, setCompare] = useState(false);
  // Local highlight while comparing; the selection is only recorded on "Select this option".
  const [selectedOption, setHighlighted] = useState(savedOption);
  const selected = loanOptions.find(o => o.id === selectedOption);
  const property = getPropertyContext(answers);
  const confirm = () => {
    if (!selected) return;
    selectOption(selected.id);
    navigate('/submit');
  };
  return <div className="min-h-screen w-full bg-canvas pb-36 md:pb-20" data-id="element-941" data-mp="3ph6hkrw3ebzl">
      <PortalHeader right={<LoanOfficerChip data-id="element-943" data-mp="3lfurkl9b3oqv" />} data-id="element-942" data-mp="3lhfm4avmf7oq" />
      <main className="mx-auto max-w-6xl px-5 pt-8 md:px-8 md:pt-14" data-id="element-944" data-mp="3lgnoyvmdl1wc">
        <div className="max-w-xl" data-id="element-945" data-mp="31v8dmlch33w">
          <JourneyStages current={2} currentProgress={50} data-id="element-946" data-mp="350wayi8lyy1" />
        </div>

        <div className="mt-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between" data-id="element-947" data-mp="3uaiwubevt6fo">
          <div className="max-w-2xl" data-id="element-948" data-mp="36kes20pyj0n3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-caution-50 px-2.5 py-1 text-xs font-medium text-caution-700" data-id="element-949" data-mp="36lgd8y0ot3ij">
              <FlaskConicalIcon className="h-3.5 w-3.5" aria-hidden data-id="element-950" data-mp="3gi2w5h9gdkhx" /> Sample data for design review
            </span>
            <h1 className="mt-4 text-[30px] font-semibold leading-tight tracking-[-0.03em] text-ink-900 md:text-[44px]" data-id="element-951" data-mp="3bhydbkl2odoj">Choose your loan</h1>
            <p className="mt-3 text-[17px] leading-relaxed text-ink-500" data-id="element-952" data-mp="qshgfdvcmndw">
              {loanOfficer.firstName} picked these {loanOptions.length} options for you based on your application. All are for a {formatCurrency(loanOptions[0].loanAmount)} loan{property.variant === 'shopping' ? '' : ` on ${property.title}`}. Choosing one doesn’t lock your rate.
            </p>
          </div>
          <Button variant="secondary" onClick={() => setCompare(c => !c)} trailingIcon={<ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${compare ? 'rotate-180' : ''}`} data-id="element-954" data-mp="24p292yg7yt97" />} aria-expanded={compare} data-id="element-953" data-mp="1zrio7fp0ni8a">
            {compare ? 'Hide comparison' : 'Compare side by side'}
          </Button>
        </div>

        <div role="radiogroup" aria-label="Loan options" className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-955" data-mp="2iojsszenrgbp">
          {loanOptions.map((o, i) => {
          const active = o.id === selectedOption;
          return <motion.button key={o.id} role="radio" aria-checked={active} onClick={() => setHighlighted(o.id)} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease,
            delay: i * 0.05
          }} className={`relative grid grid-rows-[auto_auto_1fr_auto] rounded-3xl bg-white p-6 text-left ring-1 ring-inset transition-[box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pulse-500 ${active ? 'shadow-lift ring-2 ring-pulse-600' : 'ring-ink-200 hover:shadow-soft hover:ring-ink-300'}`} data-id="element-956" data-mp="3bcfqa6c1xc9j" data-mpi={i}>
                <div className="flex items-center justify-between gap-2" data-id="element-957" data-mp="267al6b0lt4mk">
                  <span className="text-sm font-medium text-pulse-700" data-id="element-958" data-mp="21f0rql0a5mfj">{o.highlight}</span>
                  <span className={`grid h-6 w-6 place-items-center rounded-full ring-1 ring-inset transition-colors duration-150 ${active ? 'bg-pulse-600 ring-pulse-600' : 'ring-ink-300'}`} aria-hidden data-id="element-959" data-mp="1whr3v9sjhitt">
                    {active && <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} data-id="element-960" data-mp="2p4v28vx4mywm" />}
                  </span>
                </div>
                <div className="mt-4" data-id="element-961" data-mp="2wivzmyr5eipg">
                  <p className="text-lg font-semibold text-ink-900" data-id="element-962" data-mp="31fcmlpmm2r0y">{o.name}</p>
                  <p className="mt-4 text-[13px] text-ink-500" data-id="element-963" data-mp="36eyzfxyj2598">Est. monthly payment</p>
                  <p className="tabular text-[40px] font-semibold leading-none tracking-[-0.035em] text-ink-900" data-id="element-964" data-mp="36hbosw6m7hwb">
                    {formatCurrency(o.monthly)}
                    <span className="text-base font-normal tracking-normal text-ink-400" data-id="element-965" data-mp="3feuc677qe8bd">/mo</span>
                  </p>
                </div>
                <dl className="mt-6 grid grid-cols-2 gap-3 border-t border-ink-100 pt-5 text-sm" data-id="element-966" data-mp="3p5gonsk5iipu">
                  <div data-id="element-967" data-mp="3u4jv9hfs4wzx">
                    <dt className="text-ink-500" data-id="element-968" data-mp="3u51n81vesp21">Rate</dt>
                    <dd className="tabular mt-0.5 font-medium text-ink-900" data-id="element-969" data-mp="3u6mgzp9wg3yu">{o.rate.toFixed(3)}%</dd>
                  </div>
                  <div data-id="element-970" data-mp="6zkhhk6685dm">
                    <dt className="text-ink-500" data-id="element-971" data-mp="6z2em4p06swq">Cash to close</dt>
                    {o.cashToClose != null ? <dd className="tabular mt-0.5 font-medium text-ink-900" data-id="element-972" data-mp="233rx2zz7y9zv">{formatCurrency(o.cashToClose)}</dd> : <dd className="mt-0.5 text-ink-400" data-id="element-973" data-mp="1yzk7l0kxsq0l">Not available yet</dd>}
                  </div>
                  <div className="col-span-2" data-id="element-974" data-mp="282bhgvy5zdq4">
                    <dt className="text-ink-500" data-id="element-975" data-mp="280i99c7wklg0">Points / credits</dt>
                    <dd className="mt-0.5 font-medium text-ink-900" data-id="element-976" data-mp="2mq6bqgrtlq5b">{o.pointsLabel}</dd>
                  </div>
                </dl>
                <p className="mt-5 text-sm leading-relaxed text-ink-500" data-id="element-977" data-mp="2hrlcokob1cig">{o.fit}</p>
                {o.recommended && <div className="mt-5 flex items-center gap-2 rounded-xl bg-pulse-50 p-2 pr-3" data-id="element-978" data-mp="2vp9wrqajsfmg">
                    <img src={loanOfficer.photo} alt="" className="h-7 w-7 rounded-full object-cover" data-id="element-979" data-mp="2qqqb7qzv95um" />
                    <span className="text-[13px] font-medium text-pulse-800" data-id="element-980" data-mp="11ffz8sgdpn1e">{loanOfficer.firstName} recommends this one</span>
                  </div>}
              </motion.button>;
        })}
        </div>

        <AnimatePresence initial={false} data-id="element-981" data-mp="3d186pjd2g7s2">
          {compare && <motion.section initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.28,
          ease
        }} className="overflow-hidden" aria-label="Comparison" data-id="element-982" data-mp="2z9jsnaj9b98g">
              <div className="mt-6 overflow-x-auto rounded-3xl bg-white ring-1 ring-ink-200" data-id="element-983" data-mp="2pb4cg2lrpkdq">
                <table className="w-full min-w-[560px] text-sm" data-id="element-984" data-mp="2uaqs0ip2jcpp">
                  <thead data-id="element-985" data-mp="2u84bhtk79mvc">
                    <tr className="border-b border-ink-100" data-id="element-986" data-mp="2gb7hjj8sbpa8">
                      <th className="p-4 text-left font-medium text-ink-500" scope="col" data-id="element-987" data-mp="2gcsc38v3n883">
                        <span className="sr-only" data-id="element-988" data-mp="2kedmmnk5vaf4">Detail</span>
                      </th>
                      {loanOptions.map(o => <th key={o.id} scope="col" className={`p-4 text-left font-semibold ${o.id === selectedOption ? 'text-pulse-700' : 'text-ink-900'}`} data-id="element-989" data-mp="ilg0tbosxi3o">
                          {o.name}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-990" data-mp="2sqvx1gra9dyc">
                    {compareRows.map(r => <tr key={r.label} className="border-b border-ink-100 last:border-0" data-id="element-991" data-mp="2sosvbzgrpwds">
                        <th scope="row" className="p-4 text-left font-normal text-ink-500" data-id="element-992" data-mp="2npzsd0z3xnrv">
                          {r.label}
                        </th>
                        {loanOptions.map(o => <td key={o.id} className={`tabular p-4 ${o.id === selectedOption ? 'bg-pulse-50/60 font-medium text-ink-900' : 'text-ink-700'}`} data-id="element-993" data-mp="2etqs5k3b2l4o">
                            {r.get(o)}
                          </td>)}
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </motion.section>}
        </AnimatePresence>

        <div className="mt-8 grid gap-2 md:max-w-2xl" data-id="element-994" data-mp="bjdno2u84qme">
          <HelpPanel question="What are points and lender credits?" data-id="element-995" data-mp="bgriuaypwp7v">
            Points are an upfront fee you pay to lower your rate. A lender credit is the opposite — you accept a slightly higher rate and the lender covers some of your closing costs.
          </HelpPanel>
          <HelpPanel question="What’s the difference between rate and APR?" data-id="element-996" data-mp="p9qxybul8hew">
            The rate determines your monthly payment. APR includes the rate plus certain fees, so it’s useful for comparing the total cost of loans.
          </HelpPanel>
        </div>
      </main>

      <AnimatePresence data-id="element-997" data-mp="1kepje734ddn">
        {selected && <motion.div initial={{
        y: 80,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 80,
        opacity: 0
      }} transition={{
        duration: 0.28,
        ease
      }} className="fixed inset-x-0 bottom-0 z-30 border-t border-ink-200 bg-white/95 px-4 pb-6 pt-3 backdrop-blur md:pb-4" data-id="element-998" data-mp="1mr89tko2rrr">
            <div className="mx-auto flex max-w-6xl flex-col gap-3 md:flex-row md:items-center md:justify-between md:px-4" data-id="element-999" data-mp="1hxmqd4d4jn5v">
              <p className="text-sm text-ink-500" data-id="element-1000" data-mp="tyn2gx7hpshm">
                <span className="font-medium text-ink-900" data-id="element-1001" data-mp="yz0y1frwn9yr">{selected.name}</span> · {formatCurrency(selected.monthly)}/mo · {loanOfficer.firstName} will confirm final pricing before your rate is locked.
              </p>
              <Button size="lg" onClick={confirm} trailingIcon={<ArrowRightIcon className="h-5 w-5" data-id="element-1003" data-mp="b1vbs2pmcb3j" />} data-id="element-1002" data-mp="17qul7l1xvixb">
                Select this option
              </Button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}